import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Alert,
  Chip,
  Divider
} from '@mui/material'
import {
  checkCustomerRegistration,
  registerCustomer,
  createQuote,
  buyPolicy
} from '../../../../lib/requests.es6.js'
import { getPartnerGuid } from '../../../../lib/access.es6'

const navy = '#173E4B'
const GENDERS = ['Male', 'Female']

// Normalised purchase-quote → CreateQuotationDTO (policy-administration-complex).
function buildQuotationPayload(quote, customerGuid, deps) {
  return {
    product_guid: quote.product_guid,
    product_name: quote.product_name,
    product_type: 'Paid',
    customer_guid: customerGuid,
    partner: getPartnerGuid(),
    lapse_period: quote.lapse_period_days || 30,
    premium_granularity: quote.premium_granularity || 'month',
    premium_cardinality: quote.premium_cardinality || 1,
    user_agent: 'WebClient',
    policy_schedule_entries: deps.map((d) => ({
      first_name: d.first_name,
      last_name: d.last_name,
      msisdn: d.msisdn,
      date_of_birth: d.date_of_birth,
      gender: d.gender,
      national_id: d.national_id || undefined,
      relationship: d.relationship,
      premium_amount_in_cents: d.premium_cents || 0,
      package_code: quote.package_code || undefined,
      benefits: d.benefits || []
    }))
  }
}

// The Purchase flow: find-or-register customer → create quotation → buy policy.
function PurchaseDialog(props) {
  const { open, onClose, quote } = props

  const [deps, setDeps] = useState([])
  const [beneficiary, setBeneficiary] = useState({ name: '', msisdn: '', date_of_birth: '' })
  const [phase, setPhase] = useState('form') // form | running | done | error
  const [statusText, setStatusText] = useState('')
  const [error, setError] = useState(null)
  const [policy, setPolicy] = useState(null)

  useEffect(() => {
    if (!open || !quote) return
    setDeps(
      (quote.dependants || []).map((d) => ({
        relationship: d.relationship || 'Principal',
        first_name: d.first_name || '',
        last_name: d.last_name || '',
        msisdn: '',
        gender: '',
        date_of_birth: d.date_of_birth || '',
        national_id: '',
        premium_cents: d.premium_cents,
        benefits: d.benefits || []
      }))
    )
    setBeneficiary({ name: '', msisdn: '', date_of_birth: '' })
    setPhase('form')
    setError(null)
    setPolicy(null)
    setStatusText('')
  }, [open, quote])

  if (!quote) return null

  const setDep = (i, patch) => setDeps((list) => list.map((d, idx) => (idx === i ? { ...d, ...patch } : d)))

  const principal = deps.find((d) => d.relationship === 'Principal') || deps[0]
  const canSubmit =
    deps.length > 0 &&
    principal &&
    principal.msisdn.trim() &&
    principal.first_name.trim() &&
    deps.every((d) => d.msisdn.trim() && d.first_name.trim() && d.date_of_birth)

  const run = async () => {
    setPhase('running')
    setError(null)
    try {
      // 1. check registration status by msisdn; register only if not already registered
      setStatusText('Checking registration…')
      let customerGuid = null
      try {
        const status = (await checkCustomerRegistration(principal.msisdn)).data
        if (status && status.registered && status.guid) {
          customerGuid = status.guid
        }
      } catch (e) {
        customerGuid = null // treat as not registered → register below
      }
      if (!customerGuid) {
        setStatusText('Registering new customer…')
        const reg = await registerCustomer({
          msisdn: principal.msisdn,
          first_name: principal.first_name,
          surname: principal.last_name,
          gender: principal.gender || undefined,
          date_of_birth: principal.date_of_birth || undefined,
          national_id: principal.national_id || undefined
        })
        // register returns { customer: {...}, status, message }
        customerGuid =
          (reg.data && (reg.data.guid || (reg.data.customer && reg.data.customer.guid))) || null
      }
      if (!customerGuid) {
        throw new Error('Could not resolve a customer for the policyholder.')
      }
      const customer = { guid: customerGuid }

      // 2. create the quotation
      setStatusText('Creating quotation…')
      const q = await createQuote(buildQuotationPayload(quote, customer.guid, deps))
      const quotation = q.data
      if (!quotation || !quotation.guid) {
        throw new Error('Quotation was not created.')
      }

      // 3. purchase the policy
      setStatusText('Purchasing policy…')
      const buy = await buyPolicy({
        customer_guid: customer.guid,
        quotation_guid: quotation.guid,
        partner: getPartnerGuid(),
        channel: 'WebClient',
        beneficiary_name: beneficiary.name || undefined,
        beneficiary_msisdn: beneficiary.msisdn || undefined,
        beneficiary_date_of_birth: beneficiary.date_of_birth || undefined,
        web_agent_guid: '',
        debit: true,
        payment_method: 'Mobile_Money'
      })
      setPolicy(buy.data || {})
      setPhase('done')
    } catch (e) {
      const msg =
        (e && e.response && e.response.data && (e.response.data.message || e.response.data)) ||
        (e && e.message) ||
        'Purchase failed'
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg))
      setPhase('error')
    }
  }

  return (
    <Dialog open={open} onClose={phase === 'running' ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: navy, color: '#fff' }}>
        Simulate Purchase
        <Typography variant="body2" sx={{ opacity: 0.85 }}>
          {quote.product_name} {quote.package_name ? '· ' + quote.package_name : ''}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {phase === 'done' ? (
          <Box sx={{ py: 2 }}>
            <Alert severity="success" sx={{ mb: 2 }}>Policy purchased.</Alert>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Policy number:</Typography>
              <Chip
                label={policy.policy_number || policy.guid || 'N/A'}
                sx={{ fontWeight: 700, fontSize: 15, bgcolor: '#dcfce7', color: '#15803d' }}
              />
            </Box>
            {policy.status && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Status: {policy.status}
              </Typography>
            )}
          </Box>
        ) : phase === 'running' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5, gap: 2 }}>
            <CircularProgress sx={{ color: navy }} />
            <Typography variant="body2" color="text.secondary">{statusText}</Typography>
          </Box>
        ) : (
          <Box>
            {phase === 'error' && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: navy, mb: 1 }}>
              Members
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
              Prefilled from the quote — supply MSISDN, gender and any missing details. The Principal
              is the policyholder (found or registered by MSISDN).
            </Typography>
            <Table size="small" sx={{ mb: 3 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Relationship</TableCell>
                  <TableCell>First name</TableCell>
                  <TableCell>Surname</TableCell>
                  <TableCell>MSISDN</TableCell>
                  <TableCell>Gender</TableCell>
                  <TableCell>DOB</TableCell>
                  <TableCell>National ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deps.map((d, i) => (
                  <TableRow key={i}>
                    <TableCell>{d.relationship}</TableCell>
                    <TableCell><TextField size="small" value={d.first_name} onChange={(e) => setDep(i, { first_name: e.target.value })} /></TableCell>
                    <TableCell><TextField size="small" value={d.last_name} onChange={(e) => setDep(i, { last_name: e.target.value })} /></TableCell>
                    <TableCell><TextField size="small" value={d.msisdn} onChange={(e) => setDep(i, { msisdn: e.target.value })} placeholder="2557…" /></TableCell>
                    <TableCell>
                      <TextField select size="small" value={d.gender} onChange={(e) => setDep(i, { gender: e.target.value })} sx={{ minWidth: 90 }}>
                        <MenuItem value=""> </MenuItem>
                        {GENDERS.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                      </TextField>
                    </TableCell>
                    <TableCell><TextField size="small" type="date" value={d.date_of_birth} onChange={(e) => setDep(i, { date_of_birth: e.target.value })} InputLabelProps={{ shrink: true }} /></TableCell>
                    <TableCell><TextField size="small" value={d.national_id} onChange={(e) => setDep(i, { national_id: e.target.value })} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: navy, mb: 1 }}>
              Beneficiary (optional)
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
              <TextField size="small" label="Name" value={beneficiary.name} onChange={(e) => setBeneficiary({ ...beneficiary, name: e.target.value })} />
              <TextField size="small" label="MSISDN" value={beneficiary.msisdn} onChange={(e) => setBeneficiary({ ...beneficiary, msisdn: e.target.value })} />
              <TextField size="small" label="Date of birth" type="date" value={beneficiary.date_of_birth} onChange={(e) => setBeneficiary({ ...beneficiary, date_of_birth: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={phase === 'running'} sx={{ textTransform: 'none', color: '#6b7280' }}>
          {phase === 'done' ? 'Close' : 'Cancel'}
        </Button>
        {phase !== 'done' && (
          <Button
            variant="contained"
            onClick={run}
            disabled={phase === 'running' || !canSubmit}
            sx={{ textTransform: 'none', bgcolor: navy, '&:hover': { bgcolor: '#0f2d38' } }}
          >
            {phase === 'error' ? 'Retry Purchase' : 'Purchase'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default PurchaseDialog
